<template>
  <div>
    <p v-if="errors.custom_headers" class="tw:mb-2 tw:text-sm tw:text-red-600">
      {{ errors.custom_headers }}
    </p>

    <div v-if="rows.length" class="tw:mb-1 tw:hidden tw:gap-2 tw:text-xs tw:text-muted-foreground tw:sm:flex">
      <span class="tw:flex-1">{{ $t('Header name') }}</span>
      <span class="tw:flex-1">{{ $t('Header value') }}</span>
      <span class="tw:w-9 tw:shrink-0"></span>
    </div>

    <!-- Rows are submitted in their displayed order, so the index of a server error
         matches the row it belongs to once the form is rendered back. -->
    <div
      v-for="(row, index) in rows"
      :key="row.key"
      class="tw:mb-2 tw:flex tw:flex-col tw:gap-2 tw:sm:flex-row tw:sm:items-start"
    >
      <div class="tw:flex-1">
        <Input
          v-model="row.name"
          :name="`custom_headers[${index}][name]`"
          placeholder="x-make-apikey"
          autocomplete="off"
          :aria-label="$t('Header name')"
          :aria-invalid="Boolean(errorFor(index, 'name'))"
        />
        <p v-if="errorFor(index, 'name')" class="tw:mt-1 tw:mb-0 tw:text-sm tw:text-red-600">
          {{ errorFor(index, 'name') }}
        </p>
      </div>
      <div class="tw:flex-1">
        <!-- Values are secrets and never sent back to the browser: an empty value
             keeps the one already stored. -->
        <Input
          v-model="row.value"
          type="password"
          :name="`custom_headers[${index}][value]`"
          :placeholder="row.stored ? $t('Unchanged') : $t('Value')"
          autocomplete="new-password"
          :aria-label="$t('Header value')"
          :aria-invalid="Boolean(errorFor(index, 'value'))"
        />
        <p v-if="errorFor(index, 'value')" class="tw:mt-1 tw:mb-0 tw:text-sm tw:text-red-600">
          {{ errorFor(index, 'value') }}
        </p>
      </div>
      <Button
        type="button"
        variant="ghost-destructive"
        size="icon"
        class="tw:self-end tw:sm:self-auto"
        :aria-label="$t('Remove')"
        @click="removeRow(index)"
      >
        <Trash2 />
      </Button>
    </div>

    <Button type="button" variant="outline" size="sm" :disabled="rows.length >= max" @click="addRow">
      <Plus />
      {{ $t('Add a header') }}
    </Button>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { Plus, Trash2 } from '@lucide/vue'
import { Button } from '@/shadcn/components/ui/button'
import { Input } from '@/shadcn/components/ui/input'

interface HeaderRow {
  key: number
  name: string
  value: string
  stored: boolean
}

interface Props {
  /** Header names only: their values never leave the server. */
  headers?: Array<{ name: string, stored: boolean }>
  /** Validation messages keyed by field, such as `custom_headers.0.name`. */
  errors?: Record<string, string>
  max?: number
}

const props = withDefaults(defineProps<Props>(), {
  headers: () => [],
  errors: () => ({}),
  max: 10,
})

let nextKey = 0

const rows = ref<HeaderRow[]>(props.headers.map(header => ({
  key: nextKey++,
  name: header.name,
  value: '',
  stored: header.stored,
})))

// Errors point at the rows as submitted, which stops being true once one moves.
const errorsAreCurrent = ref(true)

function errorFor(index: number, field: 'name' | 'value'): string | undefined {
  return errorsAreCurrent.value ? props.errors[`custom_headers.${index}.${field}`] : undefined
}

function addRow(): void {
  rows.value.push({ key: nextKey++, name: '', value: '', stored: false })
}

function removeRow(index: number): void {
  rows.value.splice(index, 1)
  errorsAreCurrent.value = false
}
</script>
